import ListCard from '@Components/ListCard/ListCard';
import AccessoryAssignModal from '@Components/Modals/Accessories/AccessoryAssignModal';
import AccessoryUnassignModal from '@Components/Modals/Accessories/AccessoryUnassignModal';
import usePagination from '@Hooks/usePagination';
import AccessoriesServices from '@Services/Accessories.services.ts';
import { Accessory, AssetChange, Assignment } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import {
  PAGE_SIZE,
  PAGINATED_DEFAULT,
  PATHS,
  PERSISTENT_FILTER_PROP,
  TAB_VALUES,
  TABLE_STRUCTURES,
} from '@Utils/constants';
import {
  formatAssetChangeData,
  formatAssignmentsData,
  getAssignedEmployeeEmailOfAccessory,
  sortDescendingArrayByDate,
} from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styles from './AccessoryDetails.module.scss';

const AccessoryDetails = () => {
  const [accessoryDetails, setAccessoryDetails] = useState<Accessory>();
  const [currentPageAssignments, setCurrentPageAssignments] = useState<number>(1);
  const [currentPageHistory, setCurrentPageHistory] = useState<number>(1);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();
  const [accessoryIdToDelete, setAccessoryIdToDelete] = useState<string>('');
  const [accessoryEmployeeEmailToDelete, setAccessoryEmployeeEmailToDelete] = useState<string>('');
  const [showUnassignModal, setShowUnassignModal] = useState<boolean>(false);
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);

  const [assignments, setAssignments] = useState<PaginatedResponse<Assignment>>({
    ...PAGINATED_DEFAULT,
  });
  const [changes, setChanges] = useState<PaginatedResponse<AssetChange>>({
    ...PAGINATED_DEFAULT,
  });

  const { currentData: assignmentsData, jump: assignmentsJump } = usePagination(
    assignments.docs || [],
    PAGE_SIZE,
    undefined,
  );

  const { currentData: historyData, jump: historyJump } = usePagination(changes.docs || [], PAGE_SIZE, undefined);

  const [searchParams] = useSearchParams();

  const getAccessoryHref = () => {
    const data = searchParams.get(PERSISTENT_FILTER_PROP);
    if (!data) {
      return `${PATHS.ASSETS}?tab=${TAB_VALUES.ACCESSORIES}&page=${1}`;
    }
    return `${PATHS.ASSETS}${data}`;
  };
  useTopHat({
    activeNav: 'nav-assets',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      {
        title: 'Assets',
        href: getAccessoryHref(),
        id: 'Assets',
      },
      {
        title: id || 'Accessory',
        href: `.`,
        id: id || 'Accessory',
      },
    ],
  });

  const getAccessory = () => {
    setIsLoading(true);
    AccessoriesServices.getAccessory(id)
      .then(({ data }) => {
        const accessory = data[0];
        setAccessoryDetails(accessory);
        setAssignments((prev) => ({
          ...prev,
          docs: sortDescendingArrayByDate(accessory.assignments, 'assignmentDate'),
        }));
        setChanges((prev) => ({
          ...prev,
          docs: sortDescendingArrayByDate(accessory.assetChanges, 'createdAt'),
        }));
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getAccessory();
  }, []);

  const handleUnassignAccessory = (assignmentId: string) => {
    const employeeEmail = getAssignedEmployeeEmailOfAccessory(accessoryDetails, assignmentId);
    setAccessoryIdToDelete(accessoryDetails?.id || '');
    setAccessoryEmployeeEmailToDelete(employeeEmail);
    setShowUnassignModal(true);
  };

  const handleUnassignSuccess = () => {
    getAccessory();
    setAccessoryIdToDelete('');
    setAccessoryEmployeeEmailToDelete('');
  };

  const handleUnassignClose = () => {
    setShowUnassignModal(false);
    setAccessoryIdToDelete('');
    setAccessoryEmployeeEmailToDelete('');
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <h2>Accessory Info</h2>
          <div className='text-with-icon'>
            {Icons.Info}
            &nbsp;Name: {accessoryDetails && accessoryDetails.name}
          </div>
          <div className='text-with-icon'>
            {Icons.Team}
            &nbsp;Number of Users: {assignments && assignments.docs.length}
          </div>
          <div className='divider' />
          <Button
            type='primary'
            className='button-with-icon'
            fullWidth
            onClick={() => setShowAssignModal(true)}
            aria-label='Assign Accessory to User Button'>
            {Icons.Plus} Assign Accessory
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<Assignment>
          title='Owners'
          structure={TABLE_STRUCTURES.OWNERS}
          data={formatAssignmentsData(assignmentsData)}
          loading={isLoading}
          withActions={{
            unassign: (accessoryAssigmentId) => handleUnassignAccessory(accessoryAssigmentId),
          }}
          detailsPath={PATHS.ASSETS}
          error={errorMessage}
        />
        <div className={`col-12 content-middle content-right-md ${styles.pagination}`}>
          <Pagination
            count={assignments.docs.length}
            perPage={PAGE_SIZE}
            currentPage={currentPageAssignments - 1}
            onChange={(page) => {
              setCurrentPageAssignments(page + 1);
              assignmentsJump(page + 1);
            }}
          />
        </div>
        <ListCard<AssetChange>
          title='Change History'
          structure={TABLE_STRUCTURES.CHANGE_HISTORY}
          data={formatAssetChangeData(historyData)}
          loading={isLoading}
          detailsPath={PATHS.ASSETS}
          error={errorMessage}
        />
        <div className={`col-12 content-middle content-right-md ${styles.pagination}`}>
          <Pagination
            count={changes.docs.length}
            perPage={PAGE_SIZE}
            currentPage={currentPageHistory - 1}
            onChange={(page) => {
              setCurrentPageHistory(page + 1);
              historyJump(page + 1);
            }}
          />
        </div>
      </div>
      <AccessoryUnassignModal
        title='Remove Owner'
        description='Are you sure you want to unassign this accessory from the user?'
        employeeEmail={accessoryEmployeeEmailToDelete}
        accesoryId={accessoryIdToDelete}
        showModal={showUnassignModal}
        onClose={() => handleUnassignClose()}
        onSuccess={handleUnassignSuccess}
      />
      <AccessoryAssignModal
        showModal={showAssignModal}
        accesoryId={id}
        onClose={() => setShowAssignModal(false)}
        onSuccess={() => getAccessory()}
      />
    </div>
  );
};

export default AccessoryDetails;
